// src/components/advisor/AdvisorBillingIdentity.jsx
// Identité de facturation de l'Art Advisor (entité morale + fiscalité) — table advisor_billing_profiles.
// Sert à préremplir les documents ET à déterminer la TVA appliquée sur les missions.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
function luhn(num) {
  let sum = 0;
  const rev = num.split("").reverse();
  for (let i = 0; i < rev.length; i++) {
    let d = parseInt(rev[i], 10);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}
const sirenState = (v, t) => {
  const d = onlyDigits(v);
  if (!d) return { ok: null, msg: "" };
  if (d.length !== 9) return { ok: false, msg: t("advisorBillingIdentity.validation.sirenLength") };
  if (!luhn(d)) return { ok: false, msg: t("advisorBillingIdentity.validation.sirenInvalid") };
  return { ok: true, msg: t("advisorBillingIdentity.validation.sirenValid") };
};

const EMPTY = {
  legal_name: "", trade_name: "", legal_form: "", siren: "", siret: "",
  vat_number: "", vat_regime: "standard", default_vat_rate: "20",
  vat_exemption_mention: "", billing_email: "", address_line1: "",
  postal_code: "", city: "", country_code: "FR",
};

export default function AdvisorBillingIdentity() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("advisor_billing_profiles").select("*").eq("advisor_id", user.id).maybeSingle();
      if (data) {
        setF({
          legal_name: data.legal_name || "", trade_name: data.trade_name || "", legal_form: data.legal_form || "",
          siren: data.siren || "", siret: data.siret || "", vat_number: data.vat_number || "",
          vat_regime: data.vat_regime || "standard",
          default_vat_rate: data.default_vat_rate != null ? String(data.default_vat_rate) : "20",
          vat_exemption_mention: data.vat_exemption_mention || "",
          billing_email: data.billing_email || "", address_line1: data.address_line1 || "",
          postal_code: data.postal_code || "", city: data.city || "", country_code: data.country_code || "FR",
        });
      }
      setLoading(false);
    })();
  }, [user]);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setSaved(false); };

  // Régime → taux : franchise = 0 + mention ; standard = 20 par défaut ; custom = libre.
  const setRegime = (regime) => {
    setSaved(false);
    setF((p) => ({
      ...p,
      vat_regime: regime,
      default_vat_rate: regime === "franchise_base" ? "0" : regime === "standard" ? (p.default_vat_rate || "20") : p.default_vat_rate,
      // Mention légale française conservée telle quelle pour les factures relevant de l'art. 293 B du CGI.
      vat_exemption_mention: regime === "franchise_base" ? (p.vat_exemption_mention || "TVA non applicable, art. 293 B du CGI") : p.vat_exemption_mention,
    }));
  };

  const sv = useMemo(() => sirenState(f.siren, t), [f.siren, t]);

  async function save() {
    setError("");
    if (sv.ok === false) { setError(sv.msg); return; }
    setSaving(true);
    const payload = {
      advisor_id: user.id,
      legal_name: f.legal_name.trim() || null,
      trade_name: f.trade_name.trim() || null,
      legal_form: f.legal_form.trim() || null,
      siren: onlyDigits(f.siren) || null,
      siret: onlyDigits(f.siret) || null,
      vat_number: f.vat_number.trim().toUpperCase() || null,
      vat_regime: f.vat_regime,
      default_vat_rate: f.default_vat_rate === "" ? null : Number(f.default_vat_rate),
      vat_exemption_mention: f.vat_exemption_mention.trim() || null,
      billing_email: f.billing_email.trim() || null,
      address_line1: f.address_line1.trim() || null,
      postal_code: f.postal_code.trim() || null,
      city: f.city.trim() || null,
      country_code: f.country_code || "FR",
    };
    const { error: e } = await supabase.from("advisor_billing_profiles").upsert(payload, { onConflict: "advisor_id" });
    setSaving(false);
    if (e) { setError(e.message); return; }
    setSaved(true);
  }

  if (loading) return null;
  const isFranchise = f.vat_regime === "franchise_base";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="font-serif text-xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
        {t("advisorBillingIdentity.title")}
      </h2>
      <p className="mt-1 mb-5 max-w-2xl text-sm text-gray-500">
        {t("advisorBillingIdentity.description.before")}
        <strong>{t("advisorBillingIdentity.description.emphasis")}</strong>
        {t("advisorBillingIdentity.description.after")}
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label={t("advisorBillingIdentity.fields.legalName")}><input className="abi-in" value={f.legal_name} onChange={(e) => set("legal_name", e.target.value)} placeholder={t("advisorBillingIdentity.placeholders.legalName")} /></Field>
        <Field label={t("advisorBillingIdentity.fields.tradeName")}><input className="abi-in" value={f.trade_name} onChange={(e) => set("trade_name", e.target.value)} /></Field>
        <Field label={t("advisorBillingIdentity.fields.legalForm")}><input className="abi-in" value={f.legal_form} onChange={(e) => set("legal_form", e.target.value)} placeholder={t("advisorBillingIdentity.placeholders.legalForm")} /></Field>
        <Field label={t("advisorBillingIdentity.fields.siren")} hint={sv.msg} hintColor={sv.ok === true ? "#2f7d3a" : sv.ok === false ? "#b4443a" : "#8a8378"}>
          <input className="abi-in" inputMode="numeric" value={f.siren} onChange={(e) => set("siren", e.target.value)} placeholder="123 456 789" />
        </Field>
        <Field label={t("advisorBillingIdentity.fields.siret")}><input className="abi-in" inputMode="numeric" value={f.siret} onChange={(e) => set("siret", e.target.value)} /></Field>
        <Field label={t("advisorBillingIdentity.fields.vatNumber")}><input className="abi-in" value={f.vat_number} onChange={(e) => set("vat_number", e.target.value)} placeholder="FR..." /></Field>

        <Field label={t("advisorBillingIdentity.fields.vatRegime")}>
          <select className="abi-in" value={f.vat_regime} onChange={(e) => setRegime(e.target.value)}>
            <option value="standard">{t("advisorBillingIdentity.vatRegimes.standard")}</option>
            <option value="franchise_base">{t("advisorBillingIdentity.vatRegimes.franchiseBase")}</option>
            <option value="custom">{t("advisorBillingIdentity.vatRegimes.custom")}</option>
          </select>
        </Field>
        <Field label={t("advisorBillingIdentity.fields.defaultVatRate")}>
          <input className="abi-in" type="number" step="0.1" min="0" value={f.default_vat_rate}
            onChange={(e) => set("default_vat_rate", e.target.value)} disabled={isFranchise} />
        </Field>

        {isFranchise && (
          <div className="md:col-span-2">
            <Field label={t("advisorBillingIdentity.fields.exemptionMention")}>
              <input className="abi-in" value={f.vat_exemption_mention} onChange={(e) => set("vat_exemption_mention", e.target.value)} />
            </Field>
          </div>
        )}

        <Field label={t("advisorBillingIdentity.fields.billingEmail")}><input className="abi-in" type="email" value={f.billing_email} onChange={(e) => set("billing_email", e.target.value)} /></Field>
        <Field label={t("advisorBillingIdentity.fields.address")}><input className="abi-in" value={f.address_line1} onChange={(e) => set("address_line1", e.target.value)} /></Field>
        <Field label={t("advisorBillingIdentity.fields.postalCode")}><input className="abi-in" value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></Field>
        <Field label={t("advisorBillingIdentity.fields.city")}><input className="abi-in" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
      </div>

      {error && <div className="mt-4 text-sm text-red-700">{error}</div>}

      <div className="mt-5 flex items-center gap-3">
        <button onClick={save} disabled={saving} className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50">
          {saving ? t("advisorBillingIdentity.actions.saving") : t("advisorBillingIdentity.actions.save")}
        </button>
        {saved && <span className="text-sm text-emerald-700">✓ {t("advisorBillingIdentity.actions.saved")}</span>}
      </div>

      <style>{`.abi-in{width:100%;border:1px solid #d6d0c4;border-radius:8px;padding:9px 11px;font-size:14px;background:#fff;box-sizing:border-box}.abi-in:focus{outline:none;border-color:#c9a961}.abi-in:disabled{background:#f6f4ef;color:#9a9284}`}</style>
    </div>
  );
}

function Field({ label, hint, hintColor, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{label}</label>
      {children}
      {hint ? <div className="mt-1 text-xs" style={{ color: hintColor || "#8a8378" }}>{hint}</div> : null}
    </div>
  );
}
